/**
 * spiceVisitPlansApi — transport + react-query hooks for the SPICE
 * weekly schedule.
 *
 *   PUT    /api/t/{slug}/v1/clients/{clientId}/spice-plan
 *   DELETE /api/t/{slug}/v1/clients/{clientId}/spice-plan
 *   GET    /api/t/{slug}/v1/team/spice-week?week=YYYY-MM-DD
 *
 * The client record's OWN GET /clients/{clientId}/spice-plan exists and
 * is gated/tested server-side, but the record page never calls it: the
 * schedule summary rides the client record's ONE aggregate fetch instead
 * (vitals.spiceSchedule, api/_shared/clientRecordSnapshot.ts), the "one
 * aggregate fetch, no per-tab fetches" contract. The PUT mutation below
 * invalidates that aggregate's cache key on success so the record picks
 * up the save with no separate read.
 *
 * House pattern: clientsApi.ts fetch semantics (raw fetch + ApiError
 * normalization; TanStack Query owns caching). PUT and DELETE gate on
 * manage_client_identity server-side, the gate every schedule door reads
 * through canEditClientSchedule (src/lib/capabilities.ts); the week grid
 * gates on view_staff_tools. Callers `enabled`-gate so a denied role
 * fires no request.
 *
 * A schedule is a list of sessions. Removing one session of several is
 * a PUT without it (spicePlanWithoutSession); removing a plan's last
 * session, or the schedule from the editor's footer, is a DELETE of the
 * schedule whole, marked ?scope=schedule. spicePlanToInput is the body
 * that puts a plan back exactly as it was (the grid's Undo, the SPICE
 * tab's cover and hold saves).
 *
 * A session may name its own therapist (therapist_roster_id); the wire
 * carries it, with her name, only when she is not the client's own SPICE
 * therapist. A PUT body names the key on every session (an id, or null
 * for the client's own therapist) whenever the plan it starts from, the
 * body it builds or the body Undo puts back names one, and on none
 * otherwise (spicePlanToInput, withSessionTherapistKeys): the server
 * refuses a body that names no session therapist over a stored one
 * (409 reload_required), and a body never carries therapist_name.
 *
 * The plan's `status` on the wire is one word (active, covered, or a
 * pause's word: on_hold, vacation, hospital); this client reads a plan's
 * state through effectiveHold (src/lib/spiceHold.ts) and never reads the
 * plan-level cadence, slots, biweekly_anchor or status_reason the server
 * still sends beside the sessions. Every session this client sends names
 * week_of_month, null or not: the server refuses a PUT whose sessions do
 * not (a page loaded before monthly sessions existed).
 *
 * Each week-grid entry also carries its outcome (session / declined /
 * no note yet, null while the date is today or later, or the entry is
 * not active): the grid's quiet "what happened" mark, read from the
 * SPICE-band notes themselves, not the schedule.
 */

import { writeFetch } from './writeFetch';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { ApiError } from './api';
import { clientRecordKey } from './clientRecordApi';
import { effectiveHold } from './spiceHold';
import type { ClientRecordSnapshot } from './clientRecord';
import { practiceTodayIso } from './practiceDate';

export type SpiceVisitPlanCadence = 'weekly' | 'biweekly' | 'twice_weekly';
export type SpiceVisitPlanStatus = 'active' | 'covered' | 'on_hold' | 'vacation' | 'hospital';
export type SpiceHoldReason = 'vacation' | 'medical' | 'other';
export type SpiceSessionKind = 'spice' | 'spice_friendly_visitor';
export type SpiceSessionFrequency = 'weekly' | 'every_two_weeks' | 'monthly';
/** The week of the month a monthly session falls in: the first to fourth
 *  of its weekday (days 1-7, 8-14, 15-21, 22-28), or the last. */
export type SpiceWeekOfMonth = 'first' | 'second' | 'third' | 'fourth' | 'last';

/** One session of a plan. */
export interface SpiceScheduleSessionWire {
  weekday: number; // 1..7, Monday..Sunday
  start_time: string; // 'HH:MM'
  kind: SpiceSessionKind;
  frequency: SpiceSessionFrequency;
  /** The Monday an every-two-weeks session counts from. */
  anchor_week: string | null;
  /** The week of the month for a monthly session; null otherwise. */
  week_of_month: SpiceWeekOfMonth | null;
  /** Minutes for a Friendly Visitor session; null for SPICE. */
  duration_minutes: number | null;
  /** The session's own therapist: on the wire only when she is not the
   *  client's own SPICE therapist; in a PUT body an id, or null for the
   *  client's own therapist, on every session whenever any names one. */
  therapist_roster_id?: string | null;
  /** Her name, on the wire only; never sent in a PUT body. */
  therapist_name?: string;
}

export interface SpiceVisitPlanWire {
  /** One word for the plan's state; read through effectiveHold here. */
  status: SpiceVisitPlanStatus;
  /** The reason, note and until: null while the plan reads regular. */
  hold_reason: SpiceHoldReason | null;
  hold_note: string | null;
  status_until: string | null;
  /** The cover and her name: set only while the plan reads covered. */
  cover_roster_id: string | null;
  cover_name: string | null;
  sessions: SpiceScheduleSessionWire[];
  updated_at: string;
}

export interface ClientSpicePlanWire {
  client_id: string;
  therapist_roster_id: string | null;
  therapist_name: string | null;
  plan: SpiceVisitPlanWire | null;
  /** Whether THIS caller could save a PUT: canEditClientSchedule
   *  (manage_client_identity), the answer every schedule door gets from
   *  src/lib/capabilities.ts. */
  can_edit: boolean;
}

/** The PUT body: regular (active), covered (a cover required) or paused
 *  (on_hold, no cover). */
export interface SpiceVisitPlanInput {
  status: 'active' | 'covered' | 'on_hold';
  hold_reason: SpiceHoldReason | null;
  hold_note: string | null;
  status_until: string | null;
  cover_roster_id: string | null;
  sessions: SpiceScheduleSessionWire[];
}

/** A past active entry's outcome; null for an entry that is not active
 *  (no visit by this therapist was expected) or a date that is today or
 *  later (nothing to report on yet). */
export type SpiceGridOutcome = 'session' | 'declined' | 'no_note_yet';

export interface SpiceWeekGridEntryWire {
  client_id: string;
  client_display_name: string;
  weekday: number; // 1..7, Monday..Sunday
  start_time: string;
  duration_minutes: number | null;
  kind: SpiceSessionKind;
  frequency: SpiceSessionFrequency;
  anchor_week: string | null;
  week_of_month: SpiceWeekOfMonth | null;
  /** The entry's state on its own date: active (a visit this therapist
   *  makes), covered (the regular therapist's entry while another covers)
   *  or on_hold (paused). */
  status: 'active' | 'covered' | 'on_hold';
  /** The reason, note and until: set while the entry is not active. */
  hold_reason: SpiceHoldReason | null;
  hold_note?: string | null;
  status_until: string | null;
  /** The covering therapist's name, on a covered entry only. */
  covered_by_name: string | null;
  /** This therapist makes the visit as the plan's cover. */
  is_cover: boolean;
  /** The client's own therapist, named on a cover's entry (null when the
   *  client has no therapist of her own). */
  cover_of_name: string | null;
  outcome: SpiceGridOutcome | null;
}

export interface SpiceWeekGridTherapistWire {
  roster_id: string;
  full_name: string;
  entries: SpiceWeekGridEntryWire[];
}

export interface SpiceWeekGridWire {
  week_start: string;
  week_end: string;
  therapists: SpiceWeekGridTherapistWire[];
}

function base(slug: string): string {
  return `/api/t/${encodeURIComponent(slug)}/v1`;
}

async function parseOrThrow<T>(res: Response, label: string): Promise<T> {
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, body, `${label} → HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

/** Every reader of a client's schedule, invalidated after a PUT or a
 *  DELETE: the record page's ONE aggregate fetch (vitals.spiceSchedule,
 *  never a separate spice-plan query, so the band picks up the save with
 *  no second read); the week grid; My week's own edit-schedule loader (a
 *  standalone GET, since the home page has no client-record aggregate to
 *  piggyback on) and the home page itself (the change can move a visit
 *  to a different day or therapist); and the all-clients SPICE view (the
 *  week grid's own picker and its "Not scheduled yet" list), which
 *  carries each client's schedule words. */
function invalidateScheduleReaders(qc: QueryClient, slug: string, clientId: string): void {
  void qc.invalidateQueries({ queryKey: clientRecordKey(slug, clientId) });
  void qc.invalidateQueries({ queryKey: ['spice-week', slug] });
  void qc.invalidateQueries({ queryKey: clientSpicePlanKey(slug, clientId) });
  void qc.invalidateQueries({ queryKey: ['cm-home', slug] });
  void qc.invalidateQueries({ queryKey: ['clientsList', slug] });
}

/** Whether any session of a plan or a body names a therapist of its own. */
function namesSessionTherapist(sessions: readonly SpiceScheduleSessionWire[]): boolean {
  return sessions.some((s) => (s.therapist_roster_id ?? null) !== null);
}

/** One session as the PUT sends it, in the key order every body shares:
 *  the therapist key last, an id or null, only when `keyed`; never the
 *  name. */
function sessionInput(s: SpiceScheduleSessionWire, keyed: boolean): SpiceScheduleSessionWire {
  return {
    weekday: s.weekday,
    start_time: s.start_time,
    kind: s.kind,
    frequency: s.frequency,
    anchor_week: s.anchor_week,
    week_of_month: s.week_of_month,
    duration_minutes: s.duration_minutes,
    ...(keyed ? { therapist_roster_id: s.therapist_roster_id ?? null } : {}),
  };
}

/**
 * The forward body of a grid write and the body its Undo puts back,
 * each naming therapist_roster_id on every session (an id or null)
 * whenever either names a session therapist, so Undo of a first
 * cross-row drop sends null explicitly rather than a body the server
 * would refuse. When neither names one, both come back as they are.
 */
export function withSessionTherapistKeys(
  forward: SpiceVisitPlanInput,
  before: SpiceVisitPlanInput,
): { forward: SpiceVisitPlanInput; before: SpiceVisitPlanInput } {
  if (!namesSessionTherapist(forward.sessions) && !namesSessionTherapist(before.sessions)) return { forward, before };
  const keyed = (plan: SpiceVisitPlanInput): SpiceVisitPlanInput => ({
    ...plan,
    sessions: plan.sessions.map((s) => sessionInput(s, true)),
  });
  return { forward: keyed(forward), before: keyed(before) };
}

/** The PUT body that recreates a plan as it stands today: its state read
 *  through effectiveHold, so a cover or a pause that lapsed by date saves
 *  as regular, clearing the stale columns, and a cover travels only while
 *  the plan reads covered. */
export function spicePlanToInput(
  plan: Pick<SpiceVisitPlanWire, 'status' | 'hold_reason' | 'hold_note' | 'status_until' | 'cover_roster_id' | 'sessions'>,
  todayIso: string = practiceTodayIso(),
): SpiceVisitPlanInput {
  const state = effectiveHold(plan.status, plan.hold_reason, plan.status_until, { coverRosterId: plan.cover_roster_id }, todayIso);
  const away = state.status !== 'active';
  const keyed = namesSessionTherapist(plan.sessions);
  return {
    status: state.status,
    hold_reason: state.hold_reason,
    hold_note: away ? plan.hold_note : null,
    status_until: away ? plan.status_until : null,
    cover_roster_id: state.cover_roster_id,
    sessions: plan.sessions.map((s) => sessionInput(s, keyed)),
  };
}

/** The record aggregate's schedule (camelCase). */
export type SpiceScheduleVitals = NonNullable<ClientRecordSnapshot['vitals']['spiceSchedule']>;

/** The PUT body that recreates the record aggregate's schedule as it
 *  stands today, the camelCase twin of spicePlanToInput. */
export function spiceVitalsToInput(plan: SpiceScheduleVitals, todayIso: string = practiceTodayIso()): SpiceVisitPlanInput {
  return spicePlanToInput(
    {
      status: plan.status,
      hold_reason: plan.holdReason,
      hold_note: plan.holdNote,
      status_until: plan.statusUntil,
      cover_roster_id: plan.coverRosterId,
      sessions: plan.sessions.map((s) => ({
        weekday: s.weekday,
        start_time: s.startTime,
        kind: s.kind,
        frequency: s.frequency,
        anchor_week: s.anchorWeek,
        week_of_month: s.weekOfMonth,
        duration_minutes: s.durationMinutes,
        ...(s.therapistRosterId === undefined ? {} : { therapist_roster_id: s.therapistRosterId }),
      })),
    },
    todayIso,
  );
}

/** The standalone GET's plan in the record aggregate's camelCase shape,
 *  for a door with no record aggregate to read (My week, the week grid). */
export function spicePlanToVitals(plan: SpiceVisitPlanWire): SpiceScheduleVitals {
  return {
    status: plan.status,
    holdReason: plan.hold_reason,
    holdNote: plan.hold_note,
    statusUntil: plan.status_until,
    coverRosterId: plan.cover_roster_id,
    coverName: plan.cover_name,
    sessions: plan.sessions.map((s) => ({
      weekday: s.weekday,
      startTime: s.start_time,
      kind: s.kind,
      frequency: s.frequency,
      anchorWeek: s.anchor_week,
      weekOfMonth: s.week_of_month,
      durationMinutes: s.duration_minutes,
      ...((s.therapist_roster_id ?? null) === null
        ? {}
        : { therapistRosterId: s.therapist_roster_id as string, ...(s.therapist_name === undefined ? {} : { therapistName: s.therapist_name }) }),
    })),
  };
}

/** A plan with one session taken out, everything else untouched. */
export function spicePlanWithoutSession(input: SpiceVisitPlanInput, sessionIndex: number): SpiceVisitPlanInput {
  return { ...input, sessions: input.sessions.filter((_, i) => i !== sessionIndex) };
}

export function useSetClientSpicePlan(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { clientId: string; input: SpiceVisitPlanInput }) => {
      const url = `${base(slug)}/clients/${encodeURIComponent(vars.clientId)}/spice-plan`;
      const res = await writeFetch(url, {
        method: 'PUT',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(vars.input),
      });
      return parseOrThrow<ClientSpicePlanWire>(res, `PUT ${url}`);
    },
    onSuccess: (_d, vars) => invalidateScheduleReaders(qc, slug, vars.clientId),
  });
}

/** DELETE the schedule whole: the plan row and its sessions. The client
 *  keeps their therapist and becomes a SPICE client with no schedule.
 *  ?scope=schedule names the whole-plan removal; the server refuses a
 *  DELETE without it. */
export function useDeleteClientSpicePlan(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { clientId: string }) => {
      const url = `${base(slug)}/clients/${encodeURIComponent(vars.clientId)}/spice-plan?scope=schedule`;
      const res = await writeFetch(url, { method: 'DELETE', headers: { Accept: 'application/json' } });
      return parseOrThrow<{ deleted: boolean }>(res, `DELETE ${url}`);
    },
    onSuccess: (_d, vars) => invalidateScheduleReaders(qc, slug, vars.clientId),
  });
}

/**
 * GET /clients/{clientId}/spice-plan — the standalone read the record
 * page never calls (its aggregate already carries the schedule; see the
 * file header). SPICE Train A2's "Edit schedule" link on My week has no
 * client-record aggregate to piggyback on, so it fetches this directly,
 * gated read_clinical_record server-side (the same floor as the home
 * page itself, so a My-week viewer is never denied here).
 */
export function clientSpicePlanKey(slug: string, clientId: string): readonly unknown[] {
  return ['client-spice-plan', slug, clientId] as const;
}

export async function fetchClientSpicePlan(
  slug: string,
  clientId: string,
  signal?: AbortSignal,
): Promise<ClientSpicePlanWire> {
  const url = `${base(slug)}/clients/${encodeURIComponent(clientId)}/spice-plan`;
  const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  return parseOrThrow<ClientSpicePlanWire>(res, `GET ${url}`);
}

export function useClientSpicePlan(slug: string, clientId: string, opts: { enabled?: boolean } = {}) {
  const query = useQuery<ClientSpicePlanWire>({
    queryKey: clientSpicePlanKey(slug, clientId),
    queryFn: ({ signal }) => fetchClientSpicePlan(slug, clientId, signal),
    staleTime: 30_000,
    enabled: (opts.enabled ?? true) && slug !== '' && clientId !== '',
  });
  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}

export function spiceWeekKey(slug: string, week: string): readonly unknown[] {
  return ['spice-week', slug, week] as const;
}

export async function fetchSpiceWeek(slug: string, week: string, signal?: AbortSignal): Promise<SpiceWeekGridWire> {
  const url = `${base(slug)}/team/spice-week?week=${encodeURIComponent(week)}`;
  const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  return parseOrThrow<SpiceWeekGridWire>(res, `GET ${url}`);
}

export function useSpiceWeek(slug: string, week: string, opts: { enabled?: boolean } = {}) {
  const query = useQuery<SpiceWeekGridWire>({
    queryKey: spiceWeekKey(slug, week),
    queryFn: ({ signal }) => fetchSpiceWeek(slug, week, signal),
    staleTime: 30_000,
    enabled: (opts.enabled ?? true) && slug !== '' && week !== '',
  });
  return {
    data: query.data ?? null,
    therapists: query.data?.therapists ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
  };
}
